import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, inject } from '@angular/core';

let nextId = 0;

/**
 * One-message pop-up with an OK button, built without ng-zorro.
 *
 * Focus moves to the OK button, so NVDA reads "<name> dialog", the message
 * (aria-describedby), then "OK button". Tab stays on OK and re-announces it;
 * Escape cancels; focus goes back to the opener on close.
 *
 * The projected content is the message. Name the dialog with a short
 * `heading` (shown) or `ariaLabel` (not shown), never the message itself, or
 * the message is read twice.
 */
@Component({
  selector: 'prism-alert-dialog',
  standalone: false,
  templateUrl: './alert-dialog.component.html'
})
export class AlertDialogComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() heading?: string;
  @Input() ariaLabel?: string;
  @Input() okText = 'OK';
  @Output() ok = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  @ViewChild('dialog') dialog?: ElementRef<HTMLElement>;

  private cdr = inject(ChangeDetectorRef);

  private readonly id = ++nextId;
  readonly headingId = `prism-alert-dialog-heading-${this.id}`;
  readonly messageId = `prism-alert-dialog-message-${this.id}`;

  private previousFocus: HTMLElement | null = null;
  private inertElements: HTMLElement[] = [];
  private announceTimer?: ReturnType<typeof setTimeout>;
  tabAnnouncement = '';

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['visible'];
    if (!change || change.currentValue === !!change.previousValue) return;

    if (this.visible) {
      this.previousFocus = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        this.makeBackgroundInert();
        this.dialog?.nativeElement.querySelector<HTMLElement>('button')?.focus();
      });
    } else {
      this.restoreFocus();
    }
  }

  ngOnDestroy(): void {
    if (this.visible) this.restoreFocus();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancel.emit();
      return;
    }
    if (event.key !== 'Tab' || !this.dialog) return;

    // Only the OK button is focusable, so Tab and Shift+Tab stay on it. Focus
    // doesn't change, so screen readers stay silent; say the button again.
    event.preventDefault();
    this.dialog.nativeElement.querySelector<HTMLElement>('button')?.focus();
    this.announceOkButton();
  }

  /** Clears and refills the live region so a repeated Tab is spoken each time,
   * then empties it so reading through the dialog doesn't hit it. */
  private announceOkButton(): void {
    clearTimeout(this.announceTimer);
    this.tabAnnouncement = '';
    this.announceTimer = setTimeout(() => {
      this.setAnnouncement(`${this.okText} button`);
      this.announceTimer = setTimeout(() => this.setAnnouncement(''), 1500);
    }, 50);
  }

  // Hosts can be OnPush (the course page is), so timer updates must mark it.
  private setAnnouncement(text: string): void {
    this.tabAnnouncement = text;
    this.cdr.markForCheck();
  }

  /**
   * aria-modal alone doesn't stop a screen reader's continuous reading: after the
   * dialog it carries on into the page. Making everything outside the dialog
   * inert keeps the reader (and Tab) inside it.
   */
  private makeBackgroundInert(): void {
    for (let el = this.dialog?.nativeElement ?? null; el && el !== document.body; el = el.parentElement) {
      for (const sibling of Array.from(el.parentElement?.children ?? []) as HTMLElement[]) {
        if (sibling !== el && !sibling.inert) {
          sibling.inert = true;
          this.inertElements.push(sibling);
        }
      }
    }
  }

  private restoreFocus(): void {
    clearTimeout(this.announceTimer);
    this.tabAnnouncement = '';
    this.inertElements.forEach(el => (el.inert = false));
    this.inertElements = [];
    document.body.style.overflow = '';
    const target = this.previousFocus;
    this.previousFocus = null;
    // The opener can be gone by now (e.g. View Course is replaced by the course).
    if (target?.isConnected) setTimeout(() => target.focus());
  }
}
